import React, { useState } from 'react';
import { PageHeader } from '../components/PageBits';
import { events } from '../mock/data';
import { Calendar, ExternalLink, Sparkles } from 'lucide-react';

const tabs = ['All', 'Global Summit', "Women's Wing", 'Cultural', 'Youth', 'Charity', 'Community'];

const Events = () => {
  const [tab, setTab] = useState('All');
  const upcomingEvents = events.filter(e => e.upcoming);
  const pastEvents = events.filter(e => !e.upcoming);
  const filtered = tab === 'All' ? pastEvents : pastEvents.filter(e => e.tag === tab);

  return (
    <>
      <PageHeader kicker="Events" title="Moments that bring the" accent="community together." intro="REFOCC events are spaces for connection, service, celebration, and shared purpose. From major global gatherings to local cultural programmes, every event strengthens the bond between people and the mission they carry." />

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && tab === 'All' && (
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2 bg-gradient-to-r from-refocc-orange to-[#e8a04e] text-white text-xs font-bold uppercase tracking-[0.18em] px-4 py-1.5 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                Upcoming
              </span>
              <h2 className="font-serif text-2xl md:text-3xl text-refocc-dark">Upcoming Events</h2>
            </div>

            {upcomingEvents.map(e => (
              <div key={e.title} className="relative bg-gradient-to-br from-refocc-dark via-[#1a2e2e] to-[#0d1f1f] text-refocc-cream rounded-3xl overflow-hidden grid md:grid-cols-2 shadow-2xl">
                {/* Decorative glow */}
                <div className="absolute top-0 right-0 w-72 h-72 bg-refocc-orange/10 rounded-full blur-[100px] pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-refocc-orange/5 rounded-full blur-[80px] pointer-events-none" />

                <div className="aspect-[4/3] md:aspect-auto overflow-hidden relative">
                  <img src={e.img} alt={e.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-refocc-dark/30 md:block hidden" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center relative z-10">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-refocc-orange mb-4">
                    <Sparkles size={14} className="text-refocc-orange" />
                    Major Upcoming Event
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-tight">{e.title}</h2>
                  <p className="text-refocc-cream/70 mt-5 leading-relaxed text-base">{e.desc}</p>
                  <div className="mt-5 flex items-center gap-2 text-sm text-refocc-cream/60">
                    <Calendar size={14} /> {e.when}
                  </div>
                  {e.link && (
                    <a href={e.link} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 bg-refocc-orange text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-[#B97651] transition-colors w-fit shadow-lg shadow-refocc-orange/20">
                      Learn More <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filter Tabs */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-2 justify-center">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${tab === t ? 'bg-refocc-dark-mid text-refocc-cream' : 'bg-white border border-refocc-dark/15 text-refocc-dark/75 hover:border-refocc-dark'}`}>{t}</button>
          ))}
        </div>
      </section>

      {/* Featured Past Event */}
      {tab === 'All' && pastEvents.length > 0 && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-refocc-dark text-refocc-cream rounded-3xl overflow-hidden grid md:grid-cols-2">
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img src={pastEvents[0].img} alt={pastEvents[0].title} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="text-[10px] uppercase tracking-[0.18em] text-refocc-orange mb-3">Featured Event</div>
                <h2 className="font-serif text-3xl md:text-4xl leading-tight">{pastEvents[0].title}</h2>
                <p className="text-refocc-cream/70 mt-4 leading-relaxed">{pastEvents[0].desc}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-refocc-cream/60">
                  <Calendar size={14} /> {pastEvents[0].when}
                </div>
                {pastEvents[0].link && (
                  <a href={pastEvents[0].link} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 bg-refocc-orange text-white px-5 py-2.5 rounded-full font-medium text-sm hover:bg-[#B97651] transition-colors w-fit">
                    View Event <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Event Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(tab === 'All' ? filtered.slice(1) : filtered).map(e => (
            <div key={e.title} className="bg-white rounded-3xl overflow-hidden border border-refocc-dark/10 hover:shadow-xl transition-all duration-300">
              <div className="aspect-[4/3] overflow-hidden"><img src={e.img} alt={e.title} className="w-full h-full object-cover img-hover" /></div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.18em] text-refocc-orange mb-2">{e.tag}</div>
                <h3 className="font-serif text-xl text-refocc-dark leading-snug">{e.title}</h3>
                <p className="text-sm text-refocc-dark/65 mt-3 leading-relaxed">{e.desc}</p>
                <div className="mt-4 pt-4 border-t border-refocc-dark/10 flex items-center gap-2 text-xs text-refocc-dark/60">
                  <Calendar size={12} /> {e.when}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default Events;

